import type { TaskRecord } from "../persistence/records"

class TaskReferenceIndex {
    private readonly taskIdByReference = new Map<string, string>()
    private readonly referencesByTaskId = new Map<string, Set<string>>()

    replace(taskId: string, references: Iterable<string>): void {
        this.remove(taskId)
        const nextReferences = new Set(references)
        if (nextReferences.size === 0) return
        this.referencesByTaskId.set(taskId, nextReferences)
        for (const reference of nextReferences) this.taskIdByReference.set(reference, taskId)
    }

    remove(taskId: string): void {
        const previousReferences = this.referencesByTaskId.get(taskId)
        if (!previousReferences) return
        for (const reference of previousReferences) {
            if (this.taskIdByReference.get(reference) === taskId) this.taskIdByReference.delete(reference)
        }
        this.referencesByTaskId.delete(taskId)
    }

    taskIdFor(reference: string): string | null {
        return this.taskIdByReference.get(reference) ?? null
    }

    references(): string[] {
        return [...this.taskIdByReference.keys()]
    }

    clear(): void {
        this.taskIdByReference.clear()
        this.referencesByTaskId.clear()
    }
}

/**
 * Authoritative bounded lookup from Nest runtime identifiers to product tasks.
 * Reindexing one changed task costs only that task's current references.
 */
export class TaskOwnershipIndex {
    private readonly sessions = new TaskReferenceIndex()

    seed(tasks: Iterable<TaskRecord>): void {
        this.clear()
        for (const task of tasks) this.reindex(task.id, task)
    }

    reindex(taskId: string, task: TaskRecord | null): void {
        if (!task) {
            this.sessions.remove(taskId)
            return
        }
        this.sessions.replace(taskId, [task.id, ...task.sessions.flatMap((session) => (session.nestSessionId ? [session.nestSessionId] : []))])
    }

    taskIdForSession(sessionId: string): string | null {
        return this.sessions.taskIdFor(sessionId)
    }

    sessionIds(): string[] {
        return this.sessions.references()
    }

    clear(): void {
        this.sessions.clear()
    }
}
